// A render crash in one page used to take the whole app with it: with no
// error boundary React unmounts the tree, so the header, the drawer and the
// way to another tune all vanished with the broken page (the auditors found
// several such crashes — a 12/8 time signature, a foreign voicing pin — all
// fixed at their source, but the next one should not blank the screen). The
// boundary wraps the routed page only, so the chrome stays usable, and it
// resets when the route changes.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '@/navigation';

interface Props {
  /** Changing this clears a caught error (the router passes the path). */
  resetKey: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorKey: string | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorKey: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // A caught error belongs to the route it happened on.
    if (state.error && state.errorKey !== null && state.errorKey !== props.resetKey) {
      return { error: null, errorKey: null };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ errorKey: this.props.resetKey });
    console.error('Page crashed', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="page-header" role="alert" data-testid="route-error">
        <h1>This page hit a problem</h1>
        <p>Your tunes and settings are safe.</p>
        <p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => this.setState({ error: null, errorKey: null })}
          >
            Try again
          </button>{' '}
          <Link className="btn" to={APP_ROUTES.home}>
            Home
          </Link>
        </p>
      </div>
    );
  }
}
